/* eslint-disable import/no-default-export, import/order, max-lines, max-params, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { api } from "jiffy-api";
import * as ActionCable from "@rails/actioncable";

import "common/jiffy/cable";
import { findIndexById } from "common/utils/helpers/find-index-by-id";
import {
  updateHTML,
  setPDPDatesLoading
} from "components/jiffy/product-details-delivery-date-simple/init-dates";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { trackRudderstackImageProcessingCompleted } from "common/utils/rudderstack/tracker.react";
import { BUSINESS_LINE } from "common/utils/app-registry/contants";
import { DTF_PROGRESS_MAPPING } from "common/utils/progress-bar/constants";
import { DEFAULT_STICKER_VALUES } from "common/utils/stickers/constants";
import { notifyError } from "common/utils/app-monitoring/notify";

import { WS_DISCONNECT_POLLING_DELAY } from "./common/constants";
import ErrorTypes from "./error-types";
// eslint-disable-next-line import/no-cycle
import { SPANGLE_MODES, spanglesStore } from "./spangles-module";
import { useStickersStore } from "./stickers-module";
import type { SpangleVariation } from "./spangles-module.atoms";
import { getRandomId, recalculateCustomSizes } from "./utils";

declare global {
  interface Window {
    App?: {
      cable?: ActionCable.Consumer;
    };
  }
}

type CableSubscription = {
  perform: (action: string, data?: Record<string, unknown>) => void;
};

/** Metadata sub-object returned by the WebSocket for artwork images. */
type WsArtworkMetadata = {
  dpi?: number;
  uploaded_on?: string;
  width_inch?: number;
  height_inch?: number;
  aspect_ratio?: number;
  white_pixels_ratio?: number;
  whitePixelsRatio?: number;
  width?: number;
  height?: number;
  errors?: unknown[];
  [key: string]: unknown;
};

/** Artwork data object returned inside DtfChannelResult (from WebSocket). */
type WsArtworkData = {
  id?: number;
  url?: string;
  name?: string;
  metadata?: WsArtworkMetadata;
  original_metadata?: WsArtworkMetadata;
  enhanced_metadata?: WsArtworkMetadata;
  original_image_url?: string;
  with_bg_url?: string;
  no_bg_url?: string;
  no_bg_no_trim_url?: string;
  original_dpi?: number;
  original_mime?: string;
  with_bg_imgix_url?: string;
  no_bg_imgix_url?: string;
  original_image_imgix_url?: string;
  filename?: string;
  filetype?: string;
  filesize?: number;
  [key: string]: unknown;
};

type DtfChannelResult = {
  success?: boolean;
  artwork_id?: number;
  artwork?: WsArtworkData;
  data?: Record<string, unknown>;
  process_data?: WsArtworkData;
  response?: Record<string, unknown>;
  algorithm?: string;
  duration?: number;
  original_artwork_url?: string;
  pages_info?: Record<string, unknown>;
  positions_json?: string;
  design_url?: string;
  spangle_csv?: string;
  original_image_url?: string;
  algorithms_size?: number;
  error?: string;
  result?: Record<string, unknown>;
  file_type?: string;
};

type DtfChannelMessage = {
  action: string;
  result: DtfChannelResult;
  data?: Record<string, unknown>;
  worker_enhancement?: unknown;
};

/** Shape of an artwork object stored in the DTF store's artworks array. */
type StoreArtwork = {
  id: number | string;
  originalIndex?: string;
  processTimeStamp?: number;
  imageProcessingCompletedEventSent?: boolean;
  enhancementTier?: string;
  isSizesEdited?: boolean;
  sizesAndQuantities?: Array<{
    preset: string;
    width: number;
    height: number;
    [key: string]: unknown;
  }>;
  spangled?: boolean;
  isLoading?: boolean;
  enhancementCompleted?: boolean;
  lastWebSocketEvent?: string | null;
  lastWebSocketEventData?: Record<string, unknown>;
  shape?: string;
  paddingSize?: string;
  metadata?: { whitePixelsRatio?: number; [key: string]: unknown };
  bgColor?: string;
  [key: string]: unknown;
};

type DtfStoreApi = Record<string, unknown> & {
  artworks: StoreArtwork[];
  businessLine: string;
  dtfChannelUuid: string;
  isSpangle: boolean;
  isSticker: boolean;
  isStickerSample: boolean;
  isStandardArtwork: boolean;
  isGoldFoil: boolean;
  showMultiSize: boolean;
  preCut: string;
  sticker: unknown;
  initialValues: { quantity: number; [key: string]: unknown };
  experiments: Record<string, unknown>;
  actionCableConnected: boolean;
  actionCableOptions: {
    artworkIds: string;
    artworkWidth: string;
    artworkHeight: string;
    processArtwork: string;
    urlPreCut: string;
    fileContentType: string;
    artworkPaddingSize: string;
    artworkBgColor: string;
    artworkColor: string;
    artworkShape: string;
  };
  validateArtworkStartTime: Record<string | number, number>;
  updateArtworkValues: (params: Record<string, unknown>) => void;
  updateArtworkValuesById: (params: Record<string, unknown>) => void;
  setActionCableConnected: (connected: boolean) => void;
  setIsRetrying: (retrying: boolean) => void;
  setValidateArtworkStartTime: (artworkId: number | string, time: number) => void;
  clearValidateArtworkStartEventInterval: (artworkId: number | string) => void;
  setValidateArtworkStartEventIntervalID: (
    artworkId: number | string,
    intervalId: ReturnType<typeof setInterval>
  ) => void;
  getArtworksPricing: () => void;
  handleWebSocketEnhancementComplete: (artworkId: number | string) => void;
  setGangsheetValidationError: (params: Record<string, unknown>) => void;
  setArtworkPricing: (params: Record<string, unknown>) => void;
  setLoadedArtwork: (params: Record<string, unknown>) => void;
  setPresetMockupColor: (params: Record<string, unknown>) => void;
  handleProcessError: (params: Record<string, unknown>) => void;
  preloadArtworks: (params: Record<string, unknown>) => void;
  cleanupPolling: () => void;
  schedulePollingFallback: (artworkId: number | string, options?: Record<string, unknown>) => void;
  // MN-4782
  setEnhancementStuck: (artworkId: number | string) => void;
  updatePrintReadyInfoState: (artworkId: number | string) => void;
  convertImageToHexColor: (params: Record<string, unknown>) => void;
  generateGoldFoilImages: (params: Record<string, unknown>) => void;
  toggleLoading: (params: unknown[]) => void;
};

const BLACK_STICKER_BACKGROUND_THRESHOLD = 50;
const IMAGE_UPLOAD_ENHANCEMENTS_STUCK_THRESHOLD = 60;

const progressMapping = DTF_PROGRESS_MAPPING as Record<
  string,
  { label: string[]; percentage: number[] }
>;

const isTrackedAction = (action: string) => progressMapping[action] !== undefined;

const setProgress = (data: DtfChannelMessage, store: DtfStoreApi) => {
  const mapping = progressMapping[data.action];
  if (!mapping) return;
  const progressIndex = store.isSpangle ? 1 : 0;
  const { label, percentage } = mapping;
  const progressLabel = label[progressIndex] || label[0];
  const progressPercentage = percentage[progressIndex] || percentage[0];

  store.updateArtworkValuesById({
    id: data.result?.artwork_id || data.data?.["artwork_id"],
    progress: progressPercentage,
    progressMessage: progressLabel
  });
};

const setLastEvent = (data: DtfChannelMessage, store: DtfStoreApi) => {
  store.updateArtworkValuesById({
    id: data.result?.artwork_id || data.data?.["artwork_id"],
    lastWebSocketEvent: data.action,
    lastWebSocketEventData: {
      result: data.result?.process_data || data.data,
      data: data.result?.data || data.data
    }
  });
};

const handleValidateArtworkProgress = (artworkId: number | string, store: DtfStoreApi) => {
  store.setValidateArtworkStartTime(artworkId, Date.now());
  const timerInterval = setInterval(() => {
    const timeElapsed = Math.floor(
      (Date.now() - (store.validateArtworkStartTime[artworkId] ?? 0)) / 1000
    );
    if (timeElapsed > IMAGE_UPLOAD_ENHANCEMENTS_STUCK_THRESHOLD) {
      store.clearValidateArtworkStartEventInterval(artworkId);

      submitDtfEvent("image_upload_enhancements_stuck", {
        dtf_channel_uuid: store.dtfChannelUuid,
        artwork_id: artworkId
      });
      // MN-4782: surface the stuck state to the user (inline amber ArtworkNotification
      // with Retry / Upload-different actions). Firing telemetry alone — as this
      // branch used to — left the user on a forever-spinner.
      store.setEnhancementStuck(artworkId);
    }
  }, 1000);

  store.setValidateArtworkStartEventIntervalID(artworkId, timerInterval);
};

const getArtwork = (
  artworkData: DtfChannelMessage,
  store: DtfStoreApi
): StoreArtwork | undefined => {
  const { result } = artworkData;

  return store.artworks.find(artwork => {
    const artworkId =
      typeof artwork.id === "string" ? (artwork.id.match(/^\d+/) || [])[0] : artwork.id;
    return (
      Number(artworkId) === Number(result?.artwork_id) ||
      (artwork.originalIndex != null &&
        artwork.originalIndex.toString() === String(result?.data?.["file_original_index"]))
    );
  });
};

// eslint-disable-next-line max-lines-per-function,max-params
const markEnhancementCompleted = async (
  store: DtfStoreApi,
  currentArtwork: StoreArtwork,
  artworkData: WsArtworkData,
  result: DtfChannelResult,
  data: DtfChannelMessage
) => {
  if (!currentArtwork || !artworkData) return;

  const { imageProcessingCompletedEventSent } = currentArtwork;

  if (store.isStandardArtwork) {
    const { showMultiSize, initialValues } = store;
    // Use currentArtwork directly - it was already found by getArtwork() which matches
    // by originalIndex when id is not yet set (race condition with HTTP response)
    const { metadata } = artworkData;
    let sizesAndQuantities: Record<string, unknown>[] = [];

    if (showMultiSize) {
      const selectedMetadata =
        currentArtwork.enhancementTier === "print_original"
          ? (artworkData.original_metadata ?? metadata)
          : (artworkData.enhanced_metadata ?? metadata);
      sizesAndQuantities = [
        {
          width: selectedMetadata?.width_inch,
          height: selectedMetadata?.height_inch,
          quantity: initialValues.quantity,
          preset: "custom",
          id: getRandomId()
        }
      ];

      if (currentArtwork.isSizesEdited) {
        sizesAndQuantities = recalculateCustomSizes(
          currentArtwork.sizesAndQuantities as Array<{
            preset: string;
            width: number;
            height: number;
            [key: string]: unknown;
          }>,
          metadata?.aspect_ratio as number
        );
      }
    }

    const artworkUpdateData: Record<string, unknown> = {
      id: currentArtwork.id,
      originalImageUrl: artworkData.original_image_url,
      enhancedWithBgUrl: artworkData.with_bg_url,
      enhancedNoBgUrl: artworkData.no_bg_url,
      enhancedDpi: artworkData.metadata?.dpi,
      enhancedOriginalDpi: artworkData.original_dpi,
      originalDpi: 300,
      sliderPreviewUrl: artworkData.no_bg_no_trim_url,
      originalMime: artworkData.original_mime,
      enhancementCompleted: true,
      sizesAndQuantities
    };

    if (artworkData.with_bg_imgix_url) {
      artworkUpdateData["enhancedWithBgUrl"] = artworkData.with_bg_imgix_url;
    }
    if (artworkData.no_bg_imgix_url) {
      artworkUpdateData["enhancedNoBgUrl"] = artworkData.no_bg_imgix_url;
    }
    artworkUpdateData["sliderPreviewUrl"] =
      artworkData.no_bg_no_trim_url || artworkData.no_bg_imgix_url;
    if (artworkData.original_image_imgix_url) {
      artworkUpdateData["originalImageUrl"] = artworkData.original_image_imgix_url;
    }

    store.updateArtworkValuesById(artworkUpdateData);

    if (store.isGoldFoil) {
      store.generateGoldFoilImages({ artworkId: currentArtwork.id });
    }

    submitDtfEvent("dtf_image_print_perfect_toggle", {
      business_line: store.businessLine,
      artwork_id: result.artwork_id,
      enhance_image: true,
      selected_by: "jiffy",
      metadata: artworkData.metadata
    });
  }

  if (store.artworks.length >= 1 && !store.artworks.some(artwork => artwork.isLoading)) {
    store.getArtworksPricing();
  }

  if (!imageProcessingCompletedEventSent) {
    submitDtfEvent("image_processing_completed", {
      payload: {
        process_data: artworkData,
        stream_name: `dtf_channel_${store.dtfChannelUuid}`,
        action_type: data.action,
        worker_enhancement: data.worker_enhancement
      },
      business_line: store.businessLine,
      artwork_id: result.artwork_id,
      trigger_event: data.action
    });

    const enhancedImageUrl =
      artworkData.no_bg_imgix_url ||
      artworkData.with_bg_imgix_url ||
      artworkData.no_bg_url ||
      artworkData.with_bg_url;
    const originalImageUrl = artworkData.original_image_imgix_url || artworkData.original_image_url;

    trackRudderstackImageProcessingCompleted({
      ...(result.artwork_id != null && { artwork_id: String(result.artwork_id) }),
      dpi: artworkData.metadata?.dpi,
      location: artworkData.metadata?.uploaded_on,
      file_name: artworkData.filename,
      file_type: artworkData.filetype,
      file_size: artworkData.filesize,
      business_line: store.businessLine,
      artworks_count: store.artworks.length,
      pre_cut_transfers: store.preCut,
      ...(enhancedImageUrl && { enhanced_image_url: enhancedImageUrl }),
      ...(originalImageUrl && { original_image_url: originalImageUrl })
    });

    if (result.artwork_id != null) {
      // acknowledgeArtwork needs the backend-assigned id — skip when unknown
      api.dtf.acknowledgeArtwork({ id: result.artwork_id }).catch(() => {
        // Silently ignore errors
      });
    }

    // Always mark local completion to prevent re-emitting image_processing_completed
    // on replayed messages. Prefer the local currentArtwork.id so this still works
    // when the backend identifier arrives via file_original_index fallback.
    const completionId = result.artwork_id ?? currentArtwork.id;
    if (completionId != null) {
      store.updateArtworkValuesById({
        id: completionId,
        imageProcessingCompletedEventSent: true
      });
      store.updatePrintReadyInfoState(completionId);
    }
  }
};

/* eslint-disable max-lines, max-lines-per-function */
export default (store: DtfStoreApi) => {
  const spangleStore = spanglesStore;
  const stickerStore = useStickersStore();
  const params = { uuid: store.dtfChannelUuid, channel: "DtfChannel" };
  const { App } = window;

  /* eslint-disable-next-line no-undef */
  const cable = App!.cable!.subscriptions.create(params, {
    async received(
      this: CableSubscription,
      /** @type {import('jiffy-api').models.cable.DtfChannel.Actions} */ data: DtfChannelMessage
    ) {
      try {
        if (!store.actionCableConnected) {
          store.setActionCableConnected(true);
        }

        const { result } = data;
        let currentArtwork = getArtwork(data, store);

        // For spangle events, artwork_id might not be in the result
        // but there's always only one artwork in the store for spangle products
        if (!currentArtwork && store.isSpangle && data.action === "spangle") {
          currentArtwork = store.artworks?.[0];
        }

        // Check timestamp mismatch, but only if both timestamps exist
        const timeStampMismatch =
          !!result?.data?.["process_time_stamp"] &&
          !!currentArtwork?.processTimeStamp &&
          String(currentArtwork.processTimeStamp) !== String(result.data["process_time_stamp"]);

        if (timeStampMismatch) return;

        if (isTrackedAction(data.action)) {
          store.setIsRetrying(false);
          setProgress(data, store);
          setLastEvent(data, store);
        }

        if (store.isSpangle && store.artworks?.[0]) {
          store.artworks[0].spangled = true;
        }

        const eventName =
          data.action === "process_dtf_artwork_started"
            ? "image_upload_enhancements_started"
            : `image_upload_${data.action}_triggered`;
        submitDtfEvent(eventName, {
          payload: {
            process_data: result,
            stream_name: `dtf_channel_${store.dtfChannelUuid}`,
            action_type: data.action,
            worker_enhancement: data.worker_enhancement
          },
          business_line: store.businessLine,
          artwork_id: result.artwork_id,
          file_type: result?.file_type
        });

        if (data.action === "spangle") {
          let spangleData: SpangleVariation;

          if (store.isSpangle && spangleStore.spangleVariations.length === 0) {
            spangleStore.prepareVariations({ isRetry: false });
          }

          if (result.success) {
            submitDtfEvent("spangle_api_call_completed", {
              algorithm: result.algorithm,
              duration: (result.duration ?? 0) / 1000,
              artwork_url: result.original_artwork_url,
              pages_info: result.pages_info,
              business_line: BUSINESS_LINE.SPANGLE
            });

            spangleData = {
              algorithm: result.algorithm ?? "",
              imageUrl: result.design_url,
              spangleCsv: result.spangle_csv,
              spanglePagesInfo: result.pages_info,
              positionsJson: result.positions_json,
              originalImageUrl: result.original_image_url,
              error: false,
              isLoading: false
            };

            spangleStore.setOriginalArtworkUrl(result.original_image_url ?? "");
          } else {
            spangleData = { error: true, algorithm: result.algorithm ?? "", isLoading: false };
            notifyError("DTF spangle variations error", {
              params: {
                error_message: result.error,
                algorithm: result.algorithm,
                pages_info: result.pages_info
              }
            });
          }

          // There are times that reset in `removeArtwork` doesn't succeed
          // Adding this reset ensures that completedSpangleData is empty before
          // we proceed.
          if (spangleStore.completedSpangleData.length === result.algorithms_size) {
            spangleStore.resetCompletedSpangleData();
          }

          spangleStore.addCompletedSpangleData(spangleData);

          if (spangleStore.completedSpangleData.length === result.algorithms_size) {
            spangleStore.showSpangleResult();
            spangleStore.setSpangleMode(SPANGLE_MODES.sizeEdited);
          }

          return;
        }

        if (data.action === "spangle_update") {
          store.updateArtworkValues({
            index: 0,
            spangledImageUrl: result.design_url
          });

          return;
        }

        if (data.action === "upload_artwork") {
          if (!data.data?.["backend_triggered_enhancement"]) {
            this.perform("process_dtf_artwork", { ...data.data, experiments: store.experiments });
          }
          return;
        }

        if (data.action === "validate_artwork") {
          if (result.success) {
            if (currentArtwork?.id != null) {
              handleValidateArtworkProgress(currentArtwork.id, store);
            }
            return;
          }

          if (currentArtwork?.id != null) {
            store.clearValidateArtworkStartEventInterval(currentArtwork.id);
            store.handleWebSocketEnhancementComplete(currentArtwork.id);
          }

          // Prefer the backend id, fall back to local currentArtwork.id
          const validatePrintReadyId = result.artwork_id ?? currentArtwork?.id;
          if (validatePrintReadyId != null) {
            store.updatePrintReadyInfoState(validatePrintReadyId);
          }

          const { imageProcessingCompletedEventSent } = currentArtwork || {};

          if (!imageProcessingCompletedEventSent) {
            submitDtfEvent("image_processing_completed", {
              payload: {
                process_data: result,
                stream_name: `dtf_channel_${store.dtfChannelUuid}`,
                action_type: data.action,
                worker_enhancement: data.worker_enhancement
              },
              business_line: store.businessLine,
              artwork_id: result.artwork_id,
              trigger_event: data.action
            });

            trackRudderstackImageProcessingCompleted({
              ...(result.artwork_id != null && { artwork_id: String(result.artwork_id) }),
              dpi: result.artwork?.metadata?.dpi,
              location: result.artwork?.metadata?.uploaded_on,
              file_name: result.artwork?.filename,
              file_type: result.artwork?.filetype,
              file_size: result.artwork?.filesize,
              artworks_count: store.artworks.length,
              business_line: store.businessLine,
              pre_cut_transfers: store.preCut,
              enhanced_image_url:
                result.artwork?.no_bg_imgix_url || result.artwork?.with_bg_imgix_url,
              original_image_url: result.artwork?.original_image_imgix_url
            });

            store.updateArtworkValuesById({
              id: result.artwork_id,
              imageProcessingCompletedEventSent: true
            });
          }
        }

        if (data.action === "process_artwork") {
          if (currentArtwork?.id != null) {
            store.clearValidateArtworkStartEventInterval(currentArtwork.id);
          }
          if (result.success) {
            return;
          }

          if (currentArtwork?.id != null) {
            store.handleWebSocketEnhancementComplete(currentArtwork.id);
          }

          store.getArtworksPricing();
          // Gate on the locally-resolved currentArtwork (getArtwork() already
          // supports the file_original_index identity fallback), not just the
          // backend artwork_id. Otherwise findIndexById's zero-on-miss silently
          // misroutes the error to artwork[0], OR the validation error is
          // dropped entirely when the message identifies the artwork via
          // file_original_index.
          if (currentArtwork?.id != null && (result.artwork?.metadata?.errors?.length ?? 0) > 0) {
            store.setGangsheetValidationError({
              index: findIndexById(store.artworks, currentArtwork.id),
              artwork: result.artwork,
              type: ErrorTypes.INVALID_GANG_SHEET
            });
          }
        }

        if (data.action === "extract_bg_metadata") {
          if (!result.success || !currentArtwork) return;

          const processData = result.process_data;
          if (processData) {
            await markEnhancementCompleted(store, currentArtwork, processData, result, data);
          }

          return;
        }

        if (data.action === "spangle_algorithm_list") {
          if (result.success) {
            return;
          }
        }

        if (data.action === "fetch_price") {
          if (result.success) {
            let { response } = result as {
              response: Record<string, unknown> & { artworks: Record<string, unknown> };
            };

            setPDPDatesLoading(true);

            if (store.showMultiSize) {
              const artworksMap: Record<string, unknown[]> = {};

              Object.keys(response.artworks).forEach(key => {
                artworksMap[key] = [(response.artworks as Record<string, unknown>)[key]];
              });

              response = { ...response, artworks: artworksMap };
            }

            store.setArtworkPricing({
              response,
              isMultiSize: store.showMultiSize
            });

            return;
          }
        }

        if (data.action === "fetch_delivery_date") {
          if (result.success) {
            setPDPDatesLoading(false);
          }

          if (!currentArtwork) return;

          const { imageProcessingCompletedEventSent } = currentArtwork;

          updateHTML(result.result);

          if (store.isSpangle) {
            spangleStore.setOriginalArtworkUrl(result.artwork?.url ?? "");
          } else if (store.isGoldFoil) {
            store.convertImageToHexColor({ artwork: result.artwork });
          } else {
            // Skip setLoadedArtwork when result.artwork has no id — the store
            // resolves the target via findIndexById(artwork.id), which returns
            // 0 on miss and would silently overwrite artwork[0]. Losing a
            // frame update is strictly better than corrupting an innocent artwork.
            if (result.artwork?.id != null) {
              store.setLoadedArtwork({ artwork: result.artwork });
            }

            // findIndexById returns 0 on miss; gate on the locally-resolved
            // currentArtwork (supports file_original_index fallback) and use
            // its id so the color lands on the correct artwork.
            if (currentArtwork?.id != null) {
              const whitePixelsRatio = Number(result.artwork?.metadata?.white_pixels_ratio || 0);
              store.setPresetMockupColor({
                index: findIndexById(store.artworks, currentArtwork.id),
                color: whitePixelsRatio > 50 ? "#000000" : "#E1E1E1"
              });
            }

            if (store.isSticker || store.isStickerSample) {
              store.artworks.forEach((artwork, artworkIndex: number) => {
                if (artwork.id) {
                  const shape = artwork.shape || DEFAULT_STICKER_VALUES.shape;
                  const paddingSize =
                    artwork.paddingSize === "no_padding"
                      ? DEFAULT_STICKER_VALUES.paddingSize
                      : artwork.paddingSize;
                  const pixelRatio = Number(artwork.metadata?.whitePixelsRatio || 0);
                  const bgColor =
                    artwork.bgColor ||
                    (pixelRatio > BLACK_STICKER_BACKGROUND_THRESHOLD
                      ? "#000000"
                      : DEFAULT_STICKER_VALUES.bgColor);

                  store.updateArtworkValuesById({
                    id: artwork.id,
                    shape,
                    bgColor,
                    paddingSize
                  });

                  // Reload artwork after updateArtworkValuesById. noUncheckedIndexedAccess
                  // types this as potentially undefined; skip the stale iteration
                  // instead of asserting non-null.
                  const updatedArtwork = store.artworks[artworkIndex];
                  if (!updatedArtwork) return;

                  stickerStore.generateStickerImage({
                    artwork: updatedArtwork as Parameters<
                      typeof stickerStore.generateStickerImage
                    >[0]["artwork"],
                    shape: shape as string,
                    bgColor: bgColor as string,
                    paddingSize: paddingSize as string
                  });
                }
              });
            }
          }

          if (store.isStandardArtwork) {
            const { showMultiSize, initialValues } = store;
            // Use currentArtwork directly - it was already found by getArtwork() which matches
            // by originalIndex when id is not yet set (race condition with HTTP response)
            const metadata = data.result.artwork?.metadata;
            let sizesAndQuantities: Record<string, unknown>[] = [];

            if (showMultiSize) {
              const originalMetadata = data.result.artwork?.original_metadata ?? metadata;
              const enhancedMetadata = data.result.artwork?.enhanced_metadata ?? metadata;
              const isPrintOriginal = currentArtwork.enhancementTier === "print_original";
              const selectedMetadata = isPrintOriginal ? originalMetadata : enhancedMetadata;

              sizesAndQuantities = [
                {
                  width: selectedMetadata?.width_inch,
                  height: selectedMetadata?.height_inch,
                  quantity: initialValues.quantity,
                  preset: "custom",
                  id: getRandomId()
                }
              ];

              if (currentArtwork.isSizesEdited) {
                sizesAndQuantities = recalculateCustomSizes(
                  currentArtwork.sizesAndQuantities as Array<{
                    preset: string;
                    width: number;
                    height: number;
                    [key: string]: unknown;
                  }>,
                  metadata?.aspect_ratio as number
                );
              }
            }

            store.updateArtworkValuesById({
              id: currentArtwork.id,
              originalImageUrl: data.result.artwork?.original_image_url,
              enhancedWithBgUrl: data.result.artwork?.with_bg_url,
              enhancedNoBgUrl: data.result.artwork?.no_bg_url,
              enhancedDpi: data.result.artwork?.metadata?.dpi,
              enhancedOriginalDpi: data.result.artwork?.original_dpi,
              originalDpi: 300,
              sliderPreviewUrl: data.result.artwork?.no_bg_no_trim_url,
              originalMime: data.result.artwork?.original_mime,
              enhancementCompleted: true,
              sizesAndQuantities
            });

            store.handleWebSocketEnhancementComplete(currentArtwork.id);
            store.clearValidateArtworkStartEventInterval(currentArtwork.id);

            if (store.isGoldFoil) {
              store.generateGoldFoilImages({ artworkId: currentArtwork.id });
            }

            submitDtfEvent("dtf_image_print_perfect_toggle", {
              business_line: store.businessLine,
              artwork_id: currentArtwork.id,
              enhance_image: true,
              selected_by: "jiffy",
              metadata: data.result.artwork?.metadata
            });
          }

          if (store.artworks.length >= 1 && !store.artworks.some(artwork => artwork.isLoading)) {
            store.getArtworksPricing();
          }

          if (!imageProcessingCompletedEventSent) {
            submitDtfEvent("image_processing_completed", {
              payload: {
                process_data: result,
                stream_name: `dtf_channel_${store.dtfChannelUuid}`,
                action_type: data.action,
                worker_enhancement: data.worker_enhancement
              },
              business_line: store.businessLine,
              artwork_id: result.artwork_id
            });

            // Handle both Full Enhancement (*_imgix_url) and Lightweight Pipeline (*_url) field names
            const enhancedImageUrl =
              result.artwork?.no_bg_imgix_url ||
              result.artwork?.with_bg_imgix_url ||
              result.artwork?.no_bg_url ||
              result.artwork?.with_bg_url;
            const originalImageUrl =
              result.artwork?.original_image_imgix_url || result.artwork?.original_image_url;

            trackRudderstackImageProcessingCompleted({
              ...(result.artwork_id != null && { artwork_id: String(result.artwork_id) }),
              dpi: result.artwork?.metadata?.dpi,
              location: result.artwork?.metadata?.uploaded_on,
              file_name: result.artwork?.filename,
              file_type: result.artwork?.filetype,
              file_size: result.artwork?.filesize,
              business_line: store.businessLine,
              artworks_count: store.artworks.length,
              pre_cut_transfers: store.preCut,
              ...(enhancedImageUrl && { enhanced_image_url: enhancedImageUrl }),
              ...(originalImageUrl && { original_image_url: originalImageUrl })
            });

            if (result.artwork_id != null) {
              // acknowledgeArtwork needs the backend-assigned id — skip when unknown
              api.dtf.acknowledgeArtwork({ id: result.artwork_id }).catch(() => {
                // Silently ignore errors
              });
            }

            // Always mark local completion so replayed messages don't re-emit
            // image_processing_completed. Prefer currentArtwork.id so this still
            // works when identity comes through file_original_index fallback.
            const completionId = result.artwork_id ?? currentArtwork?.id;
            if (completionId != null) {
              store.updateArtworkValuesById({
                id: completionId,
                imageProcessingCompletedEventSent: true
              });
              store.updatePrintReadyInfoState(completionId);
            }
          }

          return;
        }

        if (result?.artwork && currentArtwork) {
          await markEnhancementCompleted(store, currentArtwork, result.artwork, result, data);
        }

        if (data.action === "spangle_image") {
          spangleStore.setLastSpangledDimensions({
            width: result.artwork?.metadata?.width_inch ?? null,
            height: result.artwork?.metadata?.height_inch ?? null
          });
          store.setLoadedArtwork({ artwork: result.artwork });
          spangleStore.setOriginalArtworkUrl(result.artwork?.url ?? "");

          return;
        }

        if (!result.success) {
          const { artwork } = result;

          // Route to the locally-resolved currentArtwork so that errors
          // identified only by file_original_index still reach the user.
          // findIndexById returns 0 on miss, so gating on currentArtwork?.id
          // keeps an unattributable failure from clobbering artwork[0].
          const errorArtworkId = currentArtwork?.id ?? result.artwork_id;
          if (errorArtworkId != null) {
            store.handleProcessError({
              file: {
                originalIndex: findIndexById(store.artworks, errorArtworkId),
                name: artwork?.name,
                type: artwork?.filetype,
                size: artwork?.filesize,
                width: artwork?.metadata?.width,
                height: artwork?.metadata?.height
              },
              errorResponse: result
            });
          }
        }
      } catch (error) {
        // Prevent unhandled promise rejections from async received()
        console.error("[DtfChannel] received error:", error);
        notifyError("DtfChannel received handler error", {
          params: { error: String(error) }
        });
      }
    },
    connected(this: CableSubscription) {
      const {
        artworkIds,
        artworkWidth,
        artworkHeight,
        processArtwork,
        urlPreCut,
        fileContentType,
        artworkPaddingSize,
        artworkBgColor,
        artworkColor,
        artworkShape
      } = store.actionCableOptions;

      if (artworkIds) {
        store.preloadArtworks({
          artworkIds,
          artworkWidth,
          artworkHeight,
          processArtwork,
          urlPreCut,
          fileContentType,
          artworkPaddingSize,
          artworkBgColor,
          artworkColor,
          artworkShape
        });
      }

      if (!store.actionCableConnected) {
        store.cleanupPolling();
        store.setActionCableConnected(true);
        store.setIsRetrying(true);

        if (store.isSpangle) {
          spangleStore.resetCompletedSpangleData();
        }

        store.artworks.forEach(artwork => {
          if (
            artwork.lastWebSocketEvent &&
            artwork.lastWebSocketEventData &&
            Object.keys(artwork.lastWebSocketEventData).length > 0
          ) {
            // Reset artwork processTimeStamp
            // There will be cases where discontinued continue sending messages.
            // Rejecting it to prioritize messages coming from this process since
            // this is the source of truth.
            const processTimeStamp = Date.now();
            const wsData = artwork.lastWebSocketEventData;

            this.perform("continue_dtf_artwork_process", {
              last_process: artwork.lastWebSocketEvent,
              last_process_data: wsData["result"] as Record<string, unknown>,
              data: {
                ...(wsData["data"] as Record<string, unknown>),
                processTimeStamp,
                experiments: store.experiments
              }
            });
            store.updateArtworkValuesById({
              id: artwork.id,
              lastWebSocketEvent: null,
              lastWebSocketEventData: {},
              processTimeStamp
            });
          } else if (!artwork.enhancementCompleted && !artwork.lastWebSocketEvent) {
            // Artwork has no WS resume path — re-schedule polling so it doesn't get stuck
            store.schedulePollingFallback(artwork.id);
          }
        });
      }
    },
    disconnected() {
      store.setActionCableConnected(false);

      store.artworks.forEach(artwork => {
        if (artwork.isLoading && !artwork.enhancementCompleted) {
          store.schedulePollingFallback(artwork.id, {
            initialDelay: WS_DISCONNECT_POLLING_DELAY
          });
        }
      });
    },
    spangle(this: CableSubscription, data = {}) {
      this.perform("spangle", data);
    }
  });

  return cable;
};
/* eslint-enable max-lines, max-lines-per-function */
